import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTableSort, sortRows } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/ui/SortableTh';
import {
  Boxes,
  Search,
  Loader2,
  AlertCircle,
  Warehouse,
  RefreshCw,
  ShoppingBag,
  CloudDownload,
  CheckSquare,
  Square,
  PackageX,
  FileSpreadsheet,
  FileDown,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExternalWarehouse {
  id: string;
  name: string;
  external_source: string | null;
}

interface ExternalBalanceRow {
  product_id: string;
  sku: string | null;
  product_name: string;
  image_url: string | null;
  warehouse_id: string;
  warehouse_name: string;
  quantity: number | null;
  synced_at: string | null;
  found_in_external: boolean;
}

interface Props {
  companyId: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatQty(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return parseFloat(n.toFixed(3)).toString();
}

// ─── Export helpers ───────────────────────────────────────────────────────────

async function exportExternalBalancesToExcel(rows: ExternalBalanceRow[]) {
  const XLSX = await import('xlsx');
  const wsData = rows.map(r => ({
    'מק"ט': r.sku ?? '',
    'שם מוצר': r.product_name,
    'מחסן': r.warehouse_name,
    'כמות': r.quantity ?? 0,
    'נמצא בחיצוני': r.found_in_external ? 'כן' : 'לא',
    'סנכרון אחרון': r.synced_at ? new Date(r.synced_at).toLocaleString('he-IL') : '',
  }));
  const ws = XLSX.utils.json_to_sheet(wsData);
  ws['!cols'] = [{ wch: 14 }, { wch: 25 }, { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 18 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'מחסנים חיצוניים');
  XLSX.writeFile(wb, `מחסנים_חיצוניים_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

async function exportExternalBalancesToPDF(tableEl: HTMLElement) {
  const { exportTableToPDF } = await import('../../lib/pdfExport');
  await exportTableToPDF(tableEl, `מחסנים_חיצוניים_${new Date().toISOString().slice(0, 10)}.pdf`, { orientation: 'landscape' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ExternalWarehouseBalancesView({ companyId }: Props) {

  // ── State ─────────────────────────────────────────────────────────────────
  const [warehouses, setWarehouses] = useState<ExternalWarehouse[]>([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<ExternalBalanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [unmatchedIds, setUnmatchedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);

  // ── Load external warehouses ──────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data } = await supabase
        .from('inventory_warehouses')
        .select('id, name, external_source')
        .eq('company_id', companyId)
        .eq('is_external', true)
        .order('name');

      const whs = (data ?? []) as ExternalWarehouse[];
      setWarehouses(whs);
      setSelectedWarehouses(new Set(whs.map(w => w.id)));
    })();
  }, [companyId]);

  // ── Fetch existing external balances + all products ───────────────────────
  const fetchBalances = useCallback(async () => {
    if (!companyId || selectedWarehouses.size === 0) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const whIds = Array.from(selectedWarehouses);

      // Get all products for this company
      const { data: products, error: prodErr } = await supabase
        .from('inventory_products')
        .select('id, sku, name, image_url')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (prodErr) throw prodErr;
      if (!products || products.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Get existing external balances for selected warehouses
      const { data: balances, error: balErr } = await supabase
        .from('inventory_external_balances')
        .select('product_id, warehouse_id, quantity, synced_at')
        .eq('company_id', companyId)
        .in('warehouse_id', whIds);

      if (balErr) throw balErr;

      // Build lookup: key = `${product_id}_${warehouse_id}` => balance
      const balanceMap = new Map<string, { quantity: number; synced_at: string }>();
      for (const b of balances ?? []) {
        balanceMap.set(`${b.product_id}_${b.warehouse_id}`, {
          quantity: b.quantity,
          synced_at: b.synced_at,
        });
      }

      // Warehouse name lookup
      const whNameMap = new Map<string, string>();
      warehouses.forEach(w => whNameMap.set(w.id, w.name));

      // Build rows — one per product per selected warehouse
      const result: ExternalBalanceRow[] = [];
      for (const product of products) {
        for (const whId of whIds) {
          const bal = balanceMap.get(`${product.id}_${whId}`);
          const hasBalance = !!bal && bal.quantity !== null;
          const isUnmatched = unmatchedIds.has(product.id);

          result.push({
            product_id: product.id,
            sku: product.sku,
            product_name: product.name,
            image_url: product.image_url,
            warehouse_id: whId,
            warehouse_name: whNameMap.get(whId) ?? 'Unknown',
            quantity: hasBalance ? bal.quantity : null,
            synced_at: bal ? bal.synced_at : null,
            found_in_external: hasBalance && !isUnmatched,
          });
        }
      }

      setRows(result);

      // Get latest sync time
      if (balances && balances.length > 0) {
        const latest = balances.reduce((max: string, b) =>
          (b.synced_at && b.synced_at > max) ? b.synced_at : max, balances[0]?.synced_at ?? '');
        if (latest) setSyncedAt(latest);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`שגיאה בטעינת נתונים: ${msg}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedWarehouses, warehouses, unmatchedIds]);

  useEffect(() => { fetchBalances(); }, [fetchBalances]);

  // ── Sync external inventory ───────────────────────────────────────────────
  const handleSync = useCallback(async () => {
    if (!companyId || selectedWarehouses.size === 0) return;
    setSyncing(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-pull-inventory`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            company_id: companyId,
            warehouse_ids: Array.from(selectedWarehouses),
          }),
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errBody.error ?? `HTTP ${res.status}`);
      }

      const json = await res.json() as {
        synced: number;
        unmatched_product_ids?: string[];
        synced_at?: string;
      };

      if (json.synced_at) setSyncedAt(json.synced_at);
      if (json.unmatched_product_ids) {
        setUnmatchedIds(new Set(json.unmatched_product_ids));
      }

      // Refresh data
      await fetchBalances();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`שגיאה בסנכרון: ${msg}`);
    } finally {
      setSyncing(false);
    }
  }, [companyId, selectedWarehouses, fetchBalances]);

  // ── Toggle warehouse selection ────────────────────────────────────────────
  const toggleWarehouse = (id: string) => {
    setSelectedWarehouses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedWarehouses(new Set(warehouses.map(w => w.id)));
  const deselectAll = () => setSelectedWarehouses(new Set());

  const { sort, toggle } = useTableSort<string>('product_name', 'asc');

  // ── Filter rows ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(r => {
          const nameMatch = r.product_name.toLowerCase().includes(q);
          const skuMatch = (r.sku ?? '').toLowerCase().includes(q);
          return nameMatch || skuMatch;
        })
      : rows;
    return sortRows(base, sort.key, sort.dir);
  }, [rows, search, sort]);

  // ── Empty state: no external warehouses configured ────────────────────────
  if (warehouses.length === 0 && !loading) {
    return (
      <div className="p-6" dir="rtl">
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
          <div className="p-4 bg-gray-100 rounded-full">
            <Warehouse className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-600">אין מחסנים חיצוניים מוגדרים</h3>
          <p className="text-sm text-center max-w-md text-gray-500">
            כדי להשתמש בתכונה זו, צור מחסן חיצוני בהגדרות המחסנים (הגדרות &rarr; מחסנים)
            ובחר "מחסן חיצוני (API)" בעת ההקמה.
          </p>
        </div>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5" dir="rtl">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-xl">
            <ShoppingBag className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">מחסנים חיצוניים</h2>
            <p className="text-xs text-gray-500">
              מלאי חיצוני (Shopify ועוד) — סנכרון ידני לפי דרישה
            </p>
          </div>
        </div>

        {/* Export buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={async () => { setExporting(true); try { await exportExternalBalancesToExcel(filtered); } catch { /* ignore */ } finally { setExporting(false); } }}
            disabled={exporting || filtered.length === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" />
            אקסל
          </button>
          <button
            onClick={async () => { if (!tableRef.current) return; setExporting(true); try { await exportExternalBalancesToPDF(tableRef.current); } catch (e) { console.error(e); alert('שגיאה ביצירת PDF'); } finally { setExporting(false); } }}
            disabled={exporting || filtered.length === 0}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            PDF
          </button>
        </div>

        {/* Sync info + button */}
        <div className="flex items-center gap-3">
          {syncedAt && (
            <span className="text-xs text-gray-400">
              סנכרון אחרון:{' '}
              <span className="font-medium text-gray-600">
                {new Date(syncedAt).toLocaleString('he-IL')}
              </span>
            </span>
          )}
          <button
            onClick={handleSync}
            disabled={syncing || selectedWarehouses.size === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg
                       bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {syncing ? (
              <>
                <CloudDownload className="w-4 h-4 animate-pulse" />
                מסנכרן...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                סנכרן מלאי חיצוני
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Warehouse multi-select ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">בחר מחסנים לסנכרון:</span>
          <div className="flex items-center gap-3 text-xs">
            <button onClick={selectAll} className="text-blue-600 hover:underline">
              בחר הכל
            </button>
            <button onClick={deselectAll} className="text-gray-500 hover:underline">
              נקה בחירה
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {warehouses.map(w => {
            const selected = selectedWarehouses.has(w.id);
            return (
              <button
                key={w.id}
                onClick={() => toggleWarehouse(w.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm
                            transition-all ${selected
                              ? 'bg-purple-50 border-purple-300 text-purple-700'
                              : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'}`}
              >
                {selected
                  ? <CheckSquare className="w-4 h-4 text-purple-500" />
                  : <Square className="w-4 h-4 text-gray-300" />}
                <span className="font-medium">{w.name}</span>
                {w.external_source && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100
                                   text-gray-500 font-medium uppercase">
                    {w.external_source}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Search ──────────────────────────────────────────────────────────── */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4
                           text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="חיפוש לפי שם מוצר או מקט..."
          className="w-full pr-9 pl-3 py-2 border border-gray-200 rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-purple-400
                     focus:border-transparent"
        />
      </div>

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────────────── */}
      <div ref={tableRef} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {loading && (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען נתונים...</span>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <PackageX className="w-10 h-10 opacity-40" />
            <p className="text-sm font-medium">
              {rows.length === 0
                ? 'אין נתונים — לחץ "סנכרן מלאי חיצוני" כדי למשוך נתונים'
                : 'אין תוצאות התואמות את החיפוש'}
            </p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-gray-500
                               text-xs font-medium">
                  <th className="text-right px-4 py-3 w-14">תמונה</th>
                  <SortableTh label="מקט" sortKey="sku" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="שם מוצר" sortKey="product_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="מחסן" sortKey="warehouse_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                  <SortableTh label="יתרה חיצונית" sortKey="quantity" activeKey={sort.key} dir={sort.dir} onSort={toggle} align="left" />
                  <SortableTh label="סטטוס" sortKey="found_in_external" activeKey={sort.key} dir={sort.dir} onSort={toggle} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const notFound = !row.found_in_external && row.quantity === null;
                  const isZero = row.found_in_external && row.quantity === 0;
                  return (
                    <tr
                      key={`${row.product_id}-${row.warehouse_id}`}
                      className={`border-b border-gray-50 transition-colors
                                  ${notFound
                                    ? 'bg-red-50/40 hover:bg-red-50/60'
                                    : isZero
                                      ? 'bg-gray-50/30 hover:bg-gray-50/50'
                                      : 'hover:bg-gray-50/50'}`}
                    >
                      {/* Image */}
                      <td className="px-4 py-3">
                        {row.image_url ? (
                          <img
                            src={row.image_url}
                            alt={row.product_name}
                            className="w-9 h-9 rounded-lg object-cover border border-gray-100"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center
                                          justify-center">
                            <Boxes className="w-4 h-4 text-gray-300" />
                          </div>
                        )}
                      </td>

                      {/* SKU */}
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {row.sku ?? '—'}
                      </td>

                      {/* Product name */}
                      <td className={`px-4 py-3 font-medium ${notFound ? 'text-red-700' : 'text-gray-800'}`}>
                        {row.product_name}
                      </td>

                      {/* Warehouse */}
                      <td className="px-4 py-3 text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <ShoppingBag className="w-3 h-3 text-purple-400" />
                          {row.warehouse_name}
                        </span>
                      </td>

                      {/* Quantity */}
                      <td className="px-4 py-3 text-left">
                        {row.quantity !== null ? (
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold
                              ${row.quantity < 0
                                ? 'bg-red-100 text-red-700'
                                : row.quantity === 0
                                  ? 'bg-gray-100 text-gray-500'
                                  : 'bg-purple-100 text-purple-700'}`}
                          >
                            {formatQty(row.quantity)}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        {notFound ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5
                                         rounded-full bg-red-100 text-red-700 font-medium">
                            <PackageX className="w-3 h-3" />
                            לא נמצא במערכת החיצונית
                          </span>
                        ) : isZero ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5
                                         rounded-full bg-gray-100 text-gray-500 font-medium">
                            אפס מלאי
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5
                                         rounded-full bg-green-100 text-green-700 font-medium">
                            מסונכרן
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/60
                          flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
            <span>
              {filtered.length} שורות
              {filtered.length !== rows.length && ` (מתוך ${rows.length} סה"כ)`}
            </span>
            <span className="flex-1" />
            {/* Summary stats */}
            <span className="flex items-center gap-1 text-green-700">
              <span className="w-2 h-2 rounded-full bg-green-400" />
              מסונכרן: {filtered.filter(r => r.found_in_external && r.quantity !== null && r.quantity > 0).length}
            </span>
            <span className="flex items-center gap-1 text-red-600">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              לא נמצא: {filtered.filter(r => !r.found_in_external && r.quantity === null).length}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
